import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { Broadcast } from '../types';
import { BROADCAST_STATUS_LABEL, AUDIENCE_OPTIONS } from '../types';
import { RichContentRenderer } from './RichContentRenderer';

interface RecipientStats {
  total: number;
  notif_sent: number;
  email_sent: number;
  read: number;
  pending: number;
}

interface Props {
  broadcast: Broadcast | null;
  onClose: () => void;
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const BroadcastDetailModal = ({ broadcast: b, onClose }: Props) => {
  const [stats, setStats] = useState<RecipientStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (!b || b.status !== 'sent') { setStats(null); return; }
    const fetchStats = async () => {
      setLoadingStats(true);
      const { data } = await supabase
        .from('broadcast_recipients')
        .select('notification_sent, email_sent, read_at')
        .eq('broadcast_id', b.id);

      if (data) {
        const rows = data as { notification_sent: boolean; email_sent: boolean; read_at: string | null }[];
        setStats({
          total: rows.length,
          notif_sent: rows.filter((r) => r.notification_sent).length,
          email_sent: rows.filter((r) => r.email_sent).length,
          read: rows.filter((r) => !!r.read_at).length,
          pending: rows.filter((r) => !r.read_at).length,
        });
      }
      setLoadingStats(false);
    };
    fetchStats();
  }, [b]);

  if (!b) return null;
  const meta = BROADCAST_STATUS_LABEL[b.status];
  const audienceOpt = AUDIENCE_OPTIONS.find((o) => o.value === b.filters?.audience);
  const isHtml = b.content?.trim().startsWith('<');

  const openRate = stats && stats.notif_sent > 0
    ? Math.round((stats.read / stats.notif_sent) * 100)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#0F0F1A] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${meta.bg}`}>
              <i className={`${meta.icon} ${meta.color} text-[18px]`} />
            </div>
            <div>
              <h2 className="text-[17px] font-700 text-white">{b.title}</h2>
              <span className={`text-[11px] font-700 ${meta.color}`}>{meta.label}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Delivery metrics — only for sent broadcasts */}
          {b.status === 'sent' && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3 flex items-center gap-1.5">
                <i className="ri-bar-chart-2-line" />
                Métricas de Entrega
              </p>
              {loadingStats ? (
                <div className="flex items-center gap-2 text-white/30 text-[13px]">
                  <i className="ri-loader-4-line animate-spin" /> Carregando dados reais...
                </div>
              ) : stats ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    {
                      label: 'Total destinatários',
                      value: stats.total.toLocaleString('pt-BR'),
                      icon: 'ri-group-line',
                      color: 'text-white',
                    },
                    {
                      label: 'Notif. entregues',
                      value: stats.notif_sent.toLocaleString('pt-BR'),
                      icon: 'ri-notification-3-line',
                      color: 'text-sky-400',
                    },
                    {
                      label: 'Lidas / Abertas',
                      value: stats.read.toLocaleString('pt-BR'),
                      icon: 'ri-eye-line',
                      color: 'text-emerald-400',
                    },
                    {
                      label: 'Taxa de abertura',
                      value: openRate !== null ? `${openRate}%` : '—',
                      icon: 'ri-percent-line',
                      color: openRate !== null && openRate >= 30 ? 'text-emerald-400' : 'text-amber-400',
                    },
                  ].map((m) => (
                    <div key={m.label} className="bg-white/5 rounded-xl p-3 text-center">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 mx-auto mb-2`}>
                        <i className={`${m.icon} ${m.color} text-[15px]`} />
                      </div>
                      <p className={`text-[18px] font-700 ${m.color}`}>{m.value}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">{m.label}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-white/30">Sem dados de destinatários.</p>
              )}

              {/* Progress bar */}
              {stats && stats.notif_sent > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] text-white/30 mb-1.5">
                    <span>{stats.read} lidas</span>
                    <span>{stats.pending} pendentes</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${stats.notif_sent > 0 ? (stats.read / stats.notif_sent) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          <div className="bg-white rounded-2xl overflow-hidden border border-white/10">
            {b.image_url && (
              <div className="w-full h-48">
                <img src={b.image_url} alt="Banner" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <div className="p-6">
              {b.subtitle && <p className="text-[12px] text-gray-400 mb-1">{b.subtitle}</p>}
              <h3 className="text-[20px] font-700 text-gray-900 mb-4">{b.title}</h3>

              {isHtml ? (
                <RichContentRenderer html={b.content} theme="light" />
              ) : (
                <p className="text-[14px] text-gray-600 whitespace-pre-line leading-relaxed">{b.content}</p>
              )}

              {b.cta_label && (
                <div className="mt-5">
                  <span className="inline-block px-5 py-2.5 bg-violet-600 text-white text-[13px] font-700 rounded-xl cursor-pointer">
                    {b.cta_label} {b.cta_url ? '↗' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Canais', value: (b.channels ?? []).map((c) => c === 'internal' ? 'Interno' : 'E-mail').join(', ') || '—', icon: 'ri-send-plane-line' },
              { label: 'Segmentação', value: audienceOpt?.label ?? 'Todos', icon: 'ri-group-line' },
              { label: 'Total destinatários', value: b.total_recipients.toLocaleString('pt-BR'), icon: 'ri-user-received-2-line' },
              { label: 'Enviados com sucesso', value: b.sent_count.toLocaleString('pt-BR'), icon: 'ri-checkbox-circle-line' },
              { label: 'Falhas de entrega', value: b.failed_count.toLocaleString('pt-BR'), icon: 'ri-close-circle-line' },
              { label: 'Lidas (rastreadas)', value: (b.read_count ?? 0).toLocaleString('pt-BR'), icon: 'ri-eye-line' },
              { label: 'Enviado em', value: formatDate(b.sent_at), icon: 'ri-time-line' },
              { label: 'Criado em', value: formatDate(b.created_at), icon: 'ri-add-circle-line' },
            ].map((row) => (
              <div key={row.label} className="bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-600 mb-0.5">
                  {row.label}
                </p>
                <div className="flex items-center gap-1.5">
                  <i className={`${row.icon} text-white/30 text-[12px]`} />
                  <p className="text-[13px] font-600 text-white">{row.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          {b.filters && Object.keys(b.filters).length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">
                Filtros aplicados
              </p>
              <pre className="text-[12px] text-white/50 font-mono overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(b.filters, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
